import React, { useRef } from 'react';
import GenericInputWrapper from './GenericInputWrapper';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { makeStyles } from 'tss-react/mui';
import dayjs, { Dayjs } from 'dayjs';
import { TextField, Box } from '@mui/material';

interface IGenericDatePickerProps {
  title: string;
  value: any;
  onChange: (value: any) => void;
  error?: string;
  name: string;
  onBlur?: (name: string) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  disabled?: boolean;
  minDate?: Date | string;
  maxDate?: Date | string;
  placeholder?: string;
  helperText?: string;
  inputRef?: (ref: any) => void;
  slotProps?: any; // For newer MUI versions
}

const useStyles = makeStyles<{ error: boolean }>()((_theme, { error }) => ({
  label: {
    display: 'block'
  },
  input: {
    width: '100%'
  },
  errorInput: {
    // Custom error styles if needed
  },
  error: {
    // Custom error styles if needed
  }
}));

const GenericDatePicker: React.FC<IGenericDatePickerProps> = ({
  title,
  value,
  disabled,
  error,
  onChange,
  name,
  minDate,
  maxDate,
  onBlur = () => {},
  onKeyDown,
  placeholder,
  helperText,
  inputRef
}) => {
  const { classes, cx } = useStyles({ error: !!error });

  const inputElementRef = useRef<HTMLInputElement | null>(null);
  const datePickerRef = useRef<any>(null);

  const onValueChange = (v: Dayjs | null) => {
    if (v && dayjs(v).isValid()) {
      onChange(dayjs(v).toISOString());
    } else {
      onChange('');
    }
  };

  // Handler to open the calendar popup
  const handleInputClick = () => {
    if (datePickerRef.current && typeof datePickerRef.current.focus === 'function') {
      datePickerRef.current.focus();
    }
    // Try to trigger the calendar popup
    const button = inputElementRef.current?.parentElement?.querySelector('button');
    if (button && !disabled) {
      button.click();
    }
  };

  return (
    <GenericInputWrapper title="" error={error} name={name}>
      <Box position="relative">
        <DatePicker<Dayjs>
          value={value ? dayjs(value) : null}
          label={title}
          minDate={minDate ? dayjs(minDate) : undefined}
          maxDate={maxDate ? dayjs(maxDate) : undefined}
          disabled={disabled}
          onChange={(newValue) => onValueChange(newValue)}
          InputProps={{
            error: !!error,
            size: "medium",
          }}
          PopperProps={{
            disablePortal: true,
            anchorEl: inputElementRef.current
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              className={cx(
                classes.input,
                !!error ? classes.errorInput : undefined
              )}
              variant="outlined"
              size="medium"
              error={!!error}
              placeholder={placeholder}
              helperText={error ? undefined : helperText}
              onBlur={() => onBlur(name)}
              onKeyDown={onKeyDown}
              inputRef={(el) => {
                inputElementRef.current = el;
                if (inputRef) inputRef(el);
              }}
              onClick={handleInputClick}
              fullWidth
            />
          )}
        />
      </Box>
    </GenericInputWrapper>
  );
};

export default GenericDatePicker;